import React, { useEffect, useState } from 'react';
import { Divider, Table, Tooltip, Space, Button, Dropdown, Menu } from 'antd';
import { useMutation, useQueryClient } from 'react-query';
import { updateWebhookStatus, deleteWebhook } from '@api/notificationChannel';
import { useHistory } from 'react-router-dom';
import moment from 'moment-timezone';
import { useAtomValue } from 'jotai';
import {
  CheckCircleOutlined,
  StopOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { IWebhook } from '../types';
import { StyledErrorStatus, StyledDisabledField } from '../styled';
import { getFilterWebhookList } from '../utils';
import { activeFilterAtom } from '../webhooksAtom';

const { baseUrl } = window.conviva;

const WebhooksList: React.FC<{
  webhookList: IWebhook[];
}> = ({ webhookList }) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [currentWebhookList, setCurrentWebhookList] = useState(webhookList);
  const activeFilter = useAtomValue(activeFilterAtom);

  useEffect(() => {
    const filteredWebhookList = getFilterWebhookList(webhookList, activeFilter);
    setCurrentWebhookList(filteredWebhookList);
  }, [webhookList, activeFilter]);

  const updateWebhookStatusMutation = useMutation(updateWebhookStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries('webhooksListData');
    },
  });

  const deleteWebhookMutation = useMutation(deleteWebhook, {
    onSuccess: () => {
      queryClient.invalidateQueries('webhooksListData');
    },
  });

  const menu = (record) => {
    const showDisableButton = !record.error && !record.status;
    const showEnableButton = !record.error && record.status;
    return (
      <Menu>
        {showDisableButton ? (
          <Menu.Item onClick={() => handleEnable(record)}>
            <CheckCircleOutlined /> Enable
          </Menu.Item>
        ) : null}
        {showEnableButton ? (
          <Menu.Item onClick={() => handleDisable(record)}>
            <StopOutlined /> Disable
          </Menu.Item>
        ) : null}
        <Menu.Item onClick={() => handleEdit(record)}>
          <EditOutlined /> Edit
        </Menu.Item>
        <Menu.Item onClick={() => handleDelete(record)}>
          <DeleteOutlined /> Delete
        </Menu.Item>
      </Menu>
    );
  };

  const columns: ColumnsType<IWebhook> = [
    {
      title: 'Webhooks Name',
      dataIndex: 'name',
      render: (name: string, record: IWebhook) => {
        if (record.error || !record.status) {
          return <StyledDisabledField>{name}</StyledDisabledField>;
        } else {
          return <div>{name}</div>;
        }
      },
    },
    {
      title: 'URL',
      dataIndex: 'webhookUrl',
      render: (webhookUrl: string, record: IWebhook) => {
        if (record.error || !record.status) {
          return <StyledDisabledField>{webhookUrl}</StyledDisabledField>;
        } else {
          return <div>{webhookUrl}</div>;
        }
      },
    },
    {
      title: 'Modified Date & Time',
      dataIndex: 'updatedAt',
      defaultSortOrder: 'descend',
      // reference https://github.com/microsoft/TypeScript/issues/5710#issuecomment-157886246
      sorter: (a: IWebhook, b: IWebhook) => +new Date(a.updatedAt) - +new Date(b.updatedAt),
      render: (updatedAt: string, record: IWebhook) => {
        if (record.error || !record.status) {
          return (
            <StyledDisabledField>
              {moment(updatedAt).format('DD  MMM, YYYY HH:mm')}
            </StyledDisabledField>
          );
        } else {
          return <div>{moment(updatedAt).format('DD  MMM, YYYY HH:mm')}</div>;
        }
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      render: (status: string, record: IWebhook) => {
        if (record.error) {
          return (
            <Tooltip title={record.errorDetail} placement="top">
              <StyledErrorStatus style={{ color: 'red' }}>Error</StyledErrorStatus>
            </Tooltip>
          );
        } else {
          if (record.status) {
            return <StyledErrorStatus style={{ color: 'green' }}>Enabled</StyledErrorStatus>;
          } else {
            return <StyledErrorStatus style={{ color: 'grey' }}>Disabled</StyledErrorStatus>;
          }
        }
      },
    },
    {
      title: '',
      dataIndex: 'action',
      align: 'center',
      render: (text: string, record: IWebhook) => {
        return (
          <Space size="middle">
            <Dropdown overlay={menu(record)} trigger={['click']}>
              <Button type="link">
                <MoreOutlined />
              </Button>
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  const handleEdit = (record) => {
    history.push(`${baseUrl}/settings/webhooks/edit/${record.webhookId}`);
  };

  const handleEnable = (record) => {
    updateWebhookStatusMutation.mutate({
      id: Number(record.webhookId),
      status: 1,
    });
  };

  const handleDisable = (record) => {
    updateWebhookStatusMutation.mutate({
      id: Number(record.webhookId),
      status: 0,
    });
  };

  const handleDelete = (record) => {
    deleteWebhookMutation.mutate({
      id: Number(record.webhookId),
    });
  };

  return (
    <div>
      <Divider />
      <Table
        columns={columns}
        dataSource={currentWebhookList}
        rowKey={(record) => record.webhookId}
      />
    </div>
  );
};

export default WebhooksList;
